import { CheckCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useApproveNotification,
  useCompleteHitl,
  useMarkAllRead,
  useMarkNotificationRead,
  useNotificationInbox,
  useRejectNotification,
  useSkipHitl,
} from "@/hooks/useNotifications";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";
import {
  NEEDS_YOU_EVENT_TYPES,
  NOTIFICATION_FILTER_LABELS,
  type NotificationFilter,
} from "@/types/notifications";

import { InboxRow } from "./inbox/InboxRow";

const FILTER_ORDER: NotificationFilter[] = ["unread", "all", "approvals", "alerts"];

/**
 * The rebuilt notifications inbox (Maxwell workspace Inbox tab). Unread by
 * default; a filter Select, a Mark-all-read catch-up button, a "Needs you"
 * group of still-pending approval checkpoints with inline Approve/Reject above
 * the "Updates" feed, and cursor-paged infinite scroll. Acting on a row (or
 * following it) marks it read so the count drains as you work.
 */
export function MaxwellInbox() {
  const [filter, setFilter] = useState<NotificationFilter>("unread");
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useNotificationInbox(filter);

  const approve = useApproveNotification();
  const reject = useRejectNotification();
  const completeHitl = useCompleteHitl();
  const skipHitl = useSkipHitl();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();

  const busy =
    approve.isPending ||
    reject.isPending ||
    completeHitl.isPending ||
    skipHitl.isPending ||
    markRead.isPending;

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const unread = data?.pages[0]?.unread ?? 0;

  // A still-pending "needs you" row (checkpoint approval or HITL step, unread)
  // floats to "Needs you"; a resolved one (read) drops back into Updates.
  const isPendingNeedsYou = (n: (typeof items)[number]) =>
    NEEDS_YOU_EVENT_TYPES.includes(n.event_type) && !n.read;
  const needsYou = items.filter(isPendingNeedsYou);
  const updates = items.filter((n) => !isPendingNeedsYou(n));

  const onApprove = (id: string) =>
    approve.mutate(id, { onError: () => toast.error("Could not approve.") });
  const onReject = (id: string) =>
    reject.mutate(id, { onError: () => toast.error("Could not reject.") });
  const onCompleteHitl = (id: string) =>
    completeHitl.mutate(id, {
      onError: () => toast.error("Could not mark done."),
    });
  const onSkipHitl = (id: string) =>
    skipHitl.mutate(id, { onError: () => toast.error("Could not skip.") });
  const onMarkRead = (id: string) =>
    markRead.mutate(id, { onError: () => toast.error("Could not mark as read.") });
  const onMarkAll = () =>
    markAll.mutate(undefined, { onError: () => toast.error("Could not mark all read.") });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <Select value={filter} onValueChange={(v) => setFilter(v as NotificationFilter)}>
          <SelectTrigger className="w-40" aria-label="Filter notifications">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_ORDER.map((f) => (
              <SelectItem key={f} value={f}>
                {NOTIFICATION_FILTER_LABELS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={onMarkAll}
          disabled={markAll.isPending || unread === 0}
        >
          <CheckCheck size={14} />
          Mark all read
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CheckCheck}
          title="You're all caught up"
          description="Approval requests and agent-run updates will show up here."
        />
      ) : (
        <div className="flex flex-col gap-4">
          {needsYou.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                Needs you
              </h2>
              <div className="divide-y divide-border rounded-md border border-border">
                {needsYou.map((n) => (
                  <InboxRow
                    key={n.id}
                    notification={n}
                    variant="needs-you"
                    onApprove={onApprove}
                    onReject={onReject}
                    onCompleteHitl={onCompleteHitl}
                    onSkipHitl={onSkipHitl}
                    onMarkRead={onMarkRead}
                    busy={busy}
                  />
                ))}
              </div>
            </section>
          )}

          {updates.length > 0 && (
            <section className="flex flex-col gap-2">
              {needsYou.length > 0 && (
                <h2 className="text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                  Updates
                </h2>
              )}
              <div className="divide-y divide-border rounded-md border border-border">
                {updates.map((n) => (
                  <InboxRow
                    key={n.id}
                    notification={n}
                    variant="update"
                    onApprove={onApprove}
                    onReject={onReject}
                    onCompleteHitl={onCompleteHitl}
                    onSkipHitl={onSkipHitl}
                    onMarkRead={onMarkRead}
                    busy={busy}
                  />
                ))}
              </div>
            </section>
          )}

          <div ref={sentinelRef} />
          {isFetchingNextPage && <Skeleton className="h-16 w-full" />}
        </div>
      )}
    </div>
  );
}
